import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { secureGet, secureGetPdf } from '../../config/api';
import { formatDateTime, formatAmount } from '../../utils/format';
import Table from '../../components/Table';
import LoadingLogo from '../../components/LoadingLogo';
import SnackbarNotification from '../../components/SnackbarNotification';
import PageScroller from '../../components/PageScroller';
import OptionsMenu from '../../components/OptionsMenu';
import DownloadIcon from '../../icons/downloadB.svg';


const BillingInvoices = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const topRef = useRef(null);

  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [pageNumber, setPageNumber] = useState(0);
  const [pageSize, setPageSize] = useState(30);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const abortController = useMemo(() => new AbortController(), []);
  const { signal } = abortController;

  const grayClientId = useMemo(() => {
    try {
      const settings = JSON.parse(localStorage.getItem('settings') || '{}');
      return settings.id ?? null;
    } catch {
      return null;
    }
  }, []);
  const fetchInvoices = useCallback(async (page = 0, size = pageSize) => {
    setLoading(true);
    try {
      const res = await secureGet(`/billing-invoices/by-grayClient/${grayClientId}`, showSnackbar, {
        params: { page, size },
        signal,
      });
      const data = res?.data ?? {};
      const content = Array.isArray(data.content) ? data.content : [];
      setInvoices(content);
      console.log(content);
      setTotalPages(data.totalPages ?? 1);
      setTotalElements(data.totalElements ?? content.length);
    } catch (err) {
      if (err?.name !== 'AbortError') {
        setInvoices([]);
      }
    } finally {
      setLoading(false);
    }
  }, [pageSize, signal]);

  useEffect(() => {
    fetchInvoices(pageNumber, pageSize);
    return () => abortController.abort();
  }, [pageNumber, pageSize, abortController, fetchInvoices]);

  const handleRowClick = (row) => {
    history.push({
      pathname: `/administration/billing-invoices/details/${row.id}`,
      state: { invoice: row, page: pageNumber, size: pageSize },
    });
  };

  const handlePageChange = (page) => {
    setPageNumber(page);
    topRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setPageNumber(0);
  };

  const getStatusStyle = (status) => {
    const statusUpper = (status || '').toUpperCase();
    if (statusUpper === 'PAID' || statusUpper === 'FINALIZED') return 'bg-[#06D6A0] text-white';
    if (statusUpper === 'DRAFT') return 'bg-[#A3A3A3] text-white';
    if (statusUpper === 'OVERDUE') return 'bg-[#EF476F] text-white';
    return 'bg-[#707070] text-white';
  };

  const columns = [
    { label: t('billingInvoices.invoiceNumber') || 'Invoice number', key: 'invoiceNumber' },
    {
      label: t('billingInvoices.issuedAt') || 'Issued at',
      key: 'issuedAt',
      render: (v) => formatDateTime(v),
    },
    {
      label: t('billingInvoices.periodEnd') || 'Period end',
      key: 'periodEnd',
      render: (v) => formatDateTime(v),
    },
    {
      label: t('billingInvoices.dueDate') || 'Due date',
      key: 'dueDate',
      render: (v) => formatDateTime(v),
    },
    {
      label: t('billingInvoices.priceHt') || 'Price HT',
      key: 'priceHT',
      render: (v) => formatAmount(v),
    },
    {
      label: t('billingInvoices.priceTtc') || 'Price TTC',
      key: 'priceTTC',
      render: (v) => formatAmount(v),
    },
    {
      label: t('billingInvoices.status') || 'Status',
      key: 'status',
      render: (status) => (
        <span className={`px-2 py-1 rounded-[10px] font-semibold text-[12px] ${getStatusStyle(status)}`}>
          {t(`billingInvoiceStatus.${status}`) || '—'}
        </span>
      ),
    },
    {
      key: "actions",
      render: (_, rowData) =>
        <div onClick={(e) => e.stopPropagation()}>
          <OptionsMenu options={getOptions(rowData)} />
        </div>
    },
  ];

  const getOptions = (item) => {
    const options = [
      { label: t(`actions.downloadInvoice`), icon: DownloadIcon, action: () => handleDownload(item) },
    ];
    return options;
  };

  const handleDownload = async (item) => {
    try {
      const response = await secureGetPdf(`/billing-invoices/${item.id}/pdf`, showSnackbar);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Invoice_${item.invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      showSnackbar(t('confirmation.downloadSuccess'), 'success');
    } catch (error) {
      console.error("Failed to download invoice:", error);
    }
  };

  return (
    <div ref={topRef}>
      <Helmet>
        <title>GrayFox - {t('titles.billingInvoices')}</title>
      </Helmet>

      <div className="flex items-center mb-[15px]">
        <h1 className="text-[40px] font-semibold">{t('titles.billingInvoices')}</h1>
      </div>

      <div className="relative mt-6">
        {loading && (
          <div className="absolute top-0 left-0 w-full flex justify-center z-10">
            <LoadingLogo />
          </div>
        )}

        <Table
          columns={columns}
          data={invoices}
          onRowClick={handleRowClick}
        />

        {!loading && invoices.length === 0 && (
          <div className="">
          </div>
        )}
      </div>

      <PageScroller
        pageNumber={pageNumber}
        totalPages={totalPages}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />

      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </div>
  );
};

export default BillingInvoices;
